import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validateAccessCode, portalLogin, portalSetup, submitPortalIntake, updatePortalIntentions } from '../api/client';
import LoadingLeaf from '../components/LoadingLeaf';

export default function ClientPortalLogin() {
    const { accessCode } = useParams();
    const navigate = useNavigate();
    const [clientInfo, setClientInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState('password');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [intakeForm, setIntakeForm] = useState({ name: '', email: '' });
    // First-login "intentions" step (Veronica's design doc p9): shown once,
    // right after the password step, using the coach's own questions.
    const [answers, setAnswers] = useState([]);
    const [session, setSession] = useState(null);

    useEffect(() => {
        validateCode();
    }, [accessCode]);

    const validateCode = async () => {
        try {
            const data = await validateAccessCode(accessCode);
            if (data.detail) {
                setError('Invalid or expired access code.');
            } else {
                setClientInfo(data);
                setIntakeForm(f => ({ ...f, name: data.client_name || '', email: data.client_email || '' }));
                setStep(data.has_password ? 'password' : 'intake_name');
            }
        } catch (err) {
            setError('Invalid or expired access code.');
        } finally {
            setLoading(false);
        }
    };

    const handleIntakeNameSubmit = async () => {
        setSubmitting(true);
        try {
            // Save name + email immediately so they're captured even if the
            // client abandons the form before finishing the rest.
            // Timezone is picked up from the browser rather than asked — the
            // client can still change it later in their portal Settings.
            await submitPortalIntake(accessCode, {
                name: intakeForm.name,
                email: intakeForm.email,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
        } catch (err) {
            // Non-blocking — the client can still continue setting up their portal.
        } finally {
            setSubmitting(false);
            setStep('password');
        }
    };

    const enterPortal = () => navigate(`/portal/${accessCode}/home`);

    const handleIntentionsSubmit = async () => {
        const filled = answers.some(a => a.trim());
        if (!filled) { enterPortal(); return; }
        setSubmitting(true);
        try {
            await updatePortalIntentions(session.client_id, session.token, (clientInfo.questions || []).map((q, i) => ({
                question: q.question,
                hint: q.hint,
                answer: answers[i] || '',
            })));
        } catch (err) {
            // Non-blocking — they can add these later from My Info.
        } finally {
            setSubmitting(false);
            enterPortal();
        }
    };

    const handleSubmit = async () => {
        if (!password.trim()) { setError('Please enter a password.'); return; }
        if (!clientInfo.has_password && password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

        setSubmitting(true);
        setError('');

        try {
            let data;
            if (clientInfo.has_password) {
                data = await portalLogin({ access_code: accessCode, password });
            } else {
                data = await portalSetup({ access_code: accessCode, password, client_name: clientInfo.client_name });
            }

            if (data.detail) {
                setError(data.detail);
                return;
            }

            // Store portal session
            localStorage.setItem('portal_client_id', data.client_id);
            localStorage.setItem('portal_access_code', data.access_code);
            localStorage.setItem('portal_client_name', data.client_name);
            localStorage.setItem('portal_token', data.token);

            if ((clientInfo.questions || []).length > 0 && !clientInfo.has_intentions) {
                setSession(data);
                setAnswers(clientInfo.questions.map(() => ''));
                setStep('intentions');
                return;
            }
            enterPortal();
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div style={styles.screen}>
            <LoadingLeaf fullScreen size={72} />
        </div>
    );

    if (error && !clientInfo) return (
        <div style={styles.screen}>
            <div style={styles.card}>
                <div style={styles.logo}>Transformation Companion</div>
                <div style={styles.errorAlert}>{error}</div>
                <p style={styles.hint}>Check your access code and try again.</p>
            </div>
        </div>
    );

    if (step === 'intake_name') {
        return (
            <div style={styles.screen}>
                <div style={{ ...styles.card, maxWidth: '480px' }}>
                    <div style={styles.logo}>Transformation Companion</div>
                    <div style={styles.welcome}>Welcome</div>
                    <div style={styles.subtitle}>
                        Let's start with the basics.
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Your name</label>
                        <input
                            style={styles.input}
                            value={intakeForm.name}
                            onChange={e => setIntakeForm(f => ({ ...f, name: e.target.value }))}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Your email</label>
                        <input
                            type="email"
                            style={styles.input}
                            placeholder="you@example.com"
                            value={intakeForm.email}
                            onChange={e => setIntakeForm(f => ({ ...f, email: e.target.value }))}
                        />
                    </div>

                    <button
                        style={{ ...styles.btn, opacity: (submitting || !intakeForm.email.trim()) ? 0.5 : 1 }}
                        onClick={handleIntakeNameSubmit}
                        disabled={submitting || !intakeForm.email.trim()}
                    >
                        {submitting ? 'Please wait...' : 'Next'}
                    </button>
                    <button style={styles.skipLink} onClick={() => setStep('password')} disabled={submitting}>
                        Skip for now
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'intentions') {
        const firstName = (clientInfo?.client_name || '').split(' ')[0];
        return (
            <div style={styles.screen}>
                <div style={{ ...styles.card, maxWidth: '480px' }}>
                    <div style={styles.logo}>Transformation Companion</div>
                    <div style={styles.welcome}>Welcome{firstName ? `, ${firstName}` : ''}</div>
                    <div style={styles.subtitle}>
                        Before you dive in, a few things in your own words. There's no right answer.
                        This just helps your coach meet you where you are. You can change them anytime.
                    </div>

                    {(clientInfo.questions || []).map((q, i) => (
                        <div key={i} style={styles.formGroup}>
                            <div style={styles.questionText}>{q.question}</div>
                            {q.hint && <div style={styles.questionHint}>{q.hint}</div>}
                            <textarea
                                style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }}
                                value={answers[i] || ''}
                                onChange={e => setAnswers(a => a.map((v, k) => (k === i ? e.target.value : v)))}
                            />
                        </div>
                    ))}

                    <div style={styles.privacyNote}>🔒 Only you and your coach see this</div>
                    <button
                        style={{ ...styles.btn, opacity: submitting ? 0.5 : 1 }}
                        onClick={handleIntentionsSubmit}
                        disabled={submitting}
                    >
                        {submitting ? 'Please wait...' : 'Enter my portal'}
                    </button>
                    <button style={styles.skipLink} onClick={enterPortal} disabled={submitting}>
                        Skip for now
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.screen}>
            <div style={styles.card}>
                <div style={styles.logo}>Transformation Companion</div>
                <div style={styles.welcome}>
                    Welcome{clientInfo?.client_name ? `, ${clientInfo.client_name}` : ''}
                </div>
                <div style={styles.subtitle}>
                    {clientInfo?.has_password
                        ? 'Enter your password to access your coaching portal'
                        : 'Create a password to set up your coaching portal'
                    }
                </div>

                {error && <div style={styles.errorAlert}>{error}</div>}

                <div style={styles.formGroup}>
                    <label style={styles.label}>Password</label>
                    <input
                        type="password"
                        style={styles.input}
                        placeholder="Enter your password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    />
                </div>

                {!clientInfo?.has_password && (
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Confirm password</label>
                        <input
                            type="password"
                            style={styles.input}
                            placeholder="Confirm your password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                        />
                    </div>
                )}

                <button
                    style={{ ...styles.btn, opacity: submitting ? 0.7 : 1 }}
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Please wait...' : clientInfo?.has_password ? 'Sign in' : 'Set up portal'}
                </button>

                <div style={styles.footer}>
                    Your coaching portal is private and secure.
                </div>
            </div>
        </div>
    );
}

const styles = {
    screen: { minHeight: '100vh', background: '#F8F5F0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Figtree','Segoe UI',sans-serif" },
    card: { background: 'white', borderRadius: '16px', border: '1px solid #D8E0DC', padding: '40px', width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' },
    logo: { fontFamily: 'Georgia,serif', fontSize: '20px', fontWeight: '300', fontStyle: 'italic', color: '#5C7A6B', marginBottom: '24px', textAlign: 'center' },
    welcome: { fontFamily: 'Georgia,serif', fontSize: '26px', fontWeight: '300', color: '#2D3A35', marginBottom: '8px', textAlign: 'center' },
    subtitle: { fontSize: '14px', color: '#8C9E98', marginBottom: '28px', textAlign: 'center', lineHeight: 1.6 },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#8C9E98', marginBottom: '8px' },
    input: { width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #D8E0DC', background: '#F8F5F0', fontFamily: "'Figtree',sans-serif", fontSize: '16px', color: '#2D3A35', outline: 'none', boxSizing: 'border-box' },
    btn: { width: '100%', padding: '13px', borderRadius: '8px', background: '#5C7A6B', border: 'none', color: 'white', fontFamily: "'Figtree',sans-serif", fontSize: '15px', fontWeight: '500', cursor: 'pointer', marginTop: '8px' },
    skipLink: { width: '100%', padding: '10px', background: 'none', border: 'none', color: '#8C9E98', fontFamily: "'Figtree',sans-serif", fontSize: '13.5px', cursor: 'pointer', marginTop: '8px' },
    errorAlert: { background: '#F5EAEA', color: '#B85C5C', padding: '12px 14px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px' },
    hint: { fontSize: '13px', color: '#8C9E98', textAlign: 'center', marginTop: '16px' },
    footer: { fontSize: '12px', color: '#8C9E98', textAlign: 'center', marginTop: '20px', lineHeight: 1.6 },
    loadingText: { fontSize: '14px', color: '#8C9E98' },
    questionText: { fontSize: '15px', fontWeight: '600', color: '#2D3A35', marginBottom: '2px' },
    questionHint: { fontSize: '12.5px', color: '#8C9E98', marginBottom: '8px' },
    privacyNote: { fontSize: '12px', color: '#8C9E98', marginBottom: '4px' },
};